import type {
  ContactOrder,
  ContactOrderStep,
} from "../../../../../../../../../../api/kehrblatt/contact-orders"
import { resolveTemplateDefaults } from "../../../../../../../../_shared/_logic/outreach-template-defaults"
import type {
  ContactStepEditorMode,
  ContactStepEditorState,
  ContactStepTemplateVariant,
} from "../_shared/_logic/step-editor-contract"
import { readTargetFromStep, sanitizeTargets } from "./targets"
import { roundUpToQuarter, toInputValue } from "./time"

export type ContactStepTemplateDefaults = ReturnType<typeof resolveTemplateDefaults>

export const resolveContactStepTemplateVariant = ({
  step,
  order,
}: {
  step?: ContactOrderStep | null
  order?: ContactOrder | null
}): ContactStepTemplateVariant =>
  step?.template_key ?? (order?.reason === "RESCHEDULE" ? "reschedule" : "announcement")

export const defaultTemplateForChannel = (
  channel: ContactChannel | "",
  templates: ContactStepTemplateDefaults,
  variant: ContactStepTemplateVariant = "announcement",
) => {
  if (channel === "EMAIL") return templates.email[variant].body

  if (channel === "MOBILE_MESSAGE")
    return templates.sms[variant === "reschedule" ? "reschedule" : "announcement"].body

  return ""
}

export const defaultSubjectForChannel = (
  channel: ContactChannel | "",
  templates: ContactStepTemplateDefaults,
  variant: ContactStepTemplateVariant = "announcement",
) => (channel === "EMAIL" ? templates.email[variant].subject : "")

/** Hydrate one explicit target. A removed selection must be chosen again, not replaced silently. */
export const buildInitialEditorState = ({
  mode,
  step,
  order,
  filteredChannels,
  minDue,
  targetPools,
  templateDefaults,
}: {
  mode: ContactStepEditorMode
  step?: ContactOrderStep | null
  order?: ContactOrder | null
  filteredChannels: ContactChannel[]
  minDue: Date
  targetPools: {
    emailTargets: ContactPoint[]
    phoneTargets: ContactPoint[]
    mobilePhoneTargets: ContactPoint[]
  }
  templateDefaults: ContactStepTemplateDefaults
}): ContactStepEditorState => {
  const stepChannel = step?.channel as ContactChannel | undefined
  const channel =
    stepChannel && filteredChannels.includes(stepChannel) ? stepChannel : filteredChannels[0] || ""
  const templateVariant = resolveContactStepTemplateVariant({ step, order })
  const rawDue = toInputValue(step?.due_at ?? null) || ""
  const preferredDue = mode === "edit" ? rawDue : roundUpToQuarter(rawDue) || ""
  const target = readTargetFromStep(step)
  const validTargets = sanitizeTargets(channel, target ? [target] : [], targetPools)
  const editableBody = channel === "EMAIL" || channel === "MOBILE_MESSAGE"

  return {
    channel,
    contactPointId: validTargets[0] ?? "",
    due: preferredDue || roundUpToQuarter(toInputValue(minDue)) || "",
    title: step?.title ?? "",
    notes: step?.notes ?? "",
    templateBody: editableBody
      ? (step?.body_template_override ??
        defaultTemplateForChannel(channel, templateDefaults, templateVariant))
      : "",
    templateSubject:
      channel === "EMAIL"
        ? (step?.subject_template_override ??
          defaultSubjectForChannel(channel, templateDefaults, templateVariant))
        : "",
    templateVariant,
  }
}

/** Explicit template choices only; message kind is projected by the product owner. */
export const buildStepTemplateSelection = (
  channel: ContactChannel,
  templateKey: ContactStepTemplateVariant,
  body: string,
  subject: string,
) => ({
  template_key: templateKey,
  body_template_override:
    (channel === "EMAIL" || channel === "MOBILE_MESSAGE") && body ? body : null,
  subject_template_override: channel === "EMAIL" && subject ? subject : null,
})
